import { Check, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

interface AuthSplitShellProps {
  children: ReactNode
  footer?: ReactNode
  rightPaneClassName?: string
  contentWidthClassName?: string
  centerContent?: boolean
}

export function AuthSplitShell({
  children,
  footer,
  rightPaneClassName,
  contentWidthClassName,
  centerContent = true,
}: AuthSplitShellProps) {
  const { t } = useTranslation('auth')
  const features = [
    t('branding.feature1'),
    t('branding.feature2'),
    t('branding.feature3'),
    t('branding.feature4'),
  ]

  return (
    <div className='flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.95),_rgba(243,244,248,0.95)_38%,_rgba(236,239,245,1)_100%)] px-4 py-8 sm:px-6 sm:py-10 lg:px-8'>
      <div className='mx-auto flex w-full max-w-[1120px] flex-col gap-6'>
        <div className='flex flex-col overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-[0_18px_42px_rgba(42,49,63,0.12)] lg:min-h-[449px] lg:flex-row'>
          <aside className='relative overflow-hidden bg-[#1c1d20] px-12 py-16 text-white lg:basis-[42.857%] lg:flex-none lg:self-stretch'>
            <div className='absolute -right-10 top-8 h-36 w-36 rounded-full bg-[#FF5A1F]/12 blur-3xl' />
            <div className='absolute bottom-0 left-0 h-28 w-full bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.04))]' />
            <div className='relative flex h-full items-center'>
              <div className='flex w-full max-w-[320px] flex-col gap-0'>
                <div className='flex w-[95%] min-w-0 max-w-full items-center'>
                  <img
                    src='/auth/tigerflow-logo.png'
                    alt='TigerFlow'
                    className='h-auto w-full select-none object-contain object-left'
                  />
                </div>

                <div className='space-y-5'>
                  <p className='text-sm leading-6 text-[#B0B5C3]'>
                    {t('branding.supportingLine')}
                  </p>
                  <ul className='space-y-3 text-sm text-[#B0B5C3]'>
                    {features.map(feature => (
                      <li key={feature} className='flex items-center gap-3'>
                        <span className='flex h-4 w-4 items-center justify-center text-[#FF5A1F]'>
                          <Check className='h-3.5 w-3.5' strokeWidth={2.4} />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </aside>

          <section
            className={cn(
              'flex px-16 py-16 lg:basis-[57.143%] lg:flex-1 lg:self-stretch',
              centerContent ? 'items-center' : 'items-start',
              rightPaneClassName
            )}
          >
            <div
              className={cn(
                'mx-auto flex w-full flex-col lg:max-w-[100%]',
                contentWidthClassName
              )}
            >
              {children}
            </div>
          </section>
        </div>

        {footer}
      </div>
    </div>
  )
}

interface AuthStatusPanelProps {
  icon: LucideIcon
  iconWrapClassName: string
  iconClassName: string
  title: string
  description: string
  children: ReactNode
}

export function AuthStatusPanel({
  icon: Icon,
  iconWrapClassName,
  iconClassName,
  title,
  description,
  children,
}: AuthStatusPanelProps) {
  return (
    <div className='flex flex-col items-center gap-7 py-3 text-center'>
      <div
        className={cn(
          'flex h-14 w-14 items-center justify-center rounded-full shadow-[0_10px_25px_rgba(26,27,30,0.08)]',
          iconWrapClassName
        )}
        aria-hidden
      >
        <Icon className={cn('h-5 w-5', iconClassName)} strokeWidth={2.3} />
      </div>
      <div className='space-y-2'>
        <h1 className='font-heading text-[24px] font-semibold leading-[1.2] tracking-[-0.02em] text-[#1f2329]'>
          {title}
        </h1>
        <p className='text-sm leading-6 text-[#5f6470]'>{description}</p>
      </div>
      <div className='w-full'>{children}</div>
    </div>
  )
}

export default AuthSplitShell
